import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { PageHead, Badge, Progress } from "../../components/ui-bits";
import { useAgentClient, NeedClient } from "../../components/AgentContext";
import { metaFor, SERVICE_LIST, downloadBlob } from "../../lib/services";
import { toast } from "sonner";
import { FloppyDisk, Plus, Paperclip, Trash, FileArrowUp, DownloadSimple, File, CheckCircle } from "@phosphor-icons/react";

const STATUSES = ["In Progress", "Under Review", "Waiting for Bank Documents", "Completed"];
const inputCls = "w-full border border-black/20 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

const downloadDoc = async (id, name) => {
  try { const res = await api.get(`/documents/${id}/download`, { responseType: "blob" }); downloadBlob(res.data, name); }
  catch { toast.error("Could not download file"); }
};

function JobCard({ t, i, jobDocs, onSaved, reload, refreshDocs }) {
  const [d, setD] = useState({ progress: t.progress, status: t.status, remarks: t.remarks || "" });
  const [reqName, setReqName] = useState("");
  const done = t.status === "Completed";

  const save = async () => {
    const { data } = await api.patch(`/projects/${t.id}`, { progress: Number(d.progress), status: d.status, remarks: d.remarks });
    toast.success(`Updated: ${data.service}`); onSaved(data);
  };
  const addReq = async () => {
    if (!reqName.trim()) { toast.error("Enter a document name"); return; }
    await api.post(`/projects/${t.id}/required-docs`, { name: reqName.trim() });
    toast.success(`Requested "${reqName}" from client`); setReqName(""); reload();
  };
  const delReq = async (r) => { await api.delete(`/projects/${t.id}/required-docs/${r.id}`); reload(); };
  const deliver = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/agent/documents/upload?client_email=${encodeURIComponent(t.client_email)}&folder=${encodeURIComponent(t.category || "Other")}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Final document submitted to client"); refreshDocs();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div data-testid={`gtask-${i}`} className="card p-5">
      <div className="flex justify-between items-start mb-3 gap-3">
        <div><h3 className="font-head font-bold text-lg">{t.service}</h3>
          <p className="text-xs text-[#888]">{t.category} · Due {t.due || "—"} · Stage: {t.stage}</p></div>
        <Badge status={done ? "completed" : (t.status || "").includes("Waiting") ? "overdue" : "upcoming"}>{t.status}</Badge>
      </div>
      <div className="flex items-center gap-3 mb-4"><Progress value={t.progress} /><span className="tnum text-sm font-semibold w-12 text-right">{t.progress}%</span></div>
      {t.approval_status === "pending" && <p className="text-xs text-[#8a6d00] mb-3">Awaiting client approval</p>}

      <div className="border-t border-black/10 pt-3 mb-3">
        <p className="label mb-2 flex items-center gap-1"><FileArrowUp size={12} /> Documents required from client</p>
        <div className="space-y-1.5 mb-2">
          {(t.required_docs || []).map((r, ri) => (
            <div key={r.id} data-testid={`gtask-req-${i}-${ri}`} className="flex items-center gap-2 text-sm">
              <span className={`w-2 h-2 rounded-full ${r.status === "received" ? "bg-[#00A86B]" : "bg-[#F5A623]"}`} />
              <span className="flex-1">{r.name}{r.status === "received" && <span className="text-xs text-[#00A86B] ml-2">received · {r.uploaded_name}</span>}</span>
              {r.status === "received" && r.doc_id && <button data-testid={`gtask-req-dl-${i}-${ri}`} onClick={() => downloadDoc(r.doc_id, r.uploaded_name)} className="text-[#002FA7] hover:bg-[#002FA7]/10 p-1 rounded-sm"><DownloadSimple size={15} /></button>}
              <button data-testid={`gtask-req-del-${i}-${ri}`} onClick={() => delReq(r)} className="text-[#bbb] hover:text-[#FF3B30]"><Trash size={14} /></button>
            </div>
          ))}
          {(t.required_docs || []).length === 0 && <p className="text-xs text-[#888]">No documents requested yet.</p>}
        </div>
        <div className="flex gap-2">
          <input data-testid={`gtask-req-name-${i}`} value={reqName} onChange={(e) => setReqName(e.target.value)} placeholder="e.g. Form 16A, Bank statement" className={`${inputCls} flex-1`} />
          <button data-testid={`gtask-req-add-${i}`} onClick={addReq} className="border border-black/20 hover:bg-black/5 px-3 py-1.5 rounded-sm text-sm inline-flex items-center gap-1"><Plus size={14} /> Request</button>
        </div>
      </div>

      <div className="border-t border-black/10 pt-3 mb-3">
        <p className="label mb-2 flex items-center gap-1"><File size={12} /> Records for this service ({jobDocs.length})</p>
        {jobDocs.length === 0 ? <p className="text-xs text-[#888]">Nothing uploaded for this service yet.</p> : (
          <div className="space-y-1.5">
            {jobDocs.map((doc, di) => (
              <div key={doc.id} data-testid={`gtask-cdoc-${i}-${di}`} className="flex items-center gap-2 text-sm">
                <File size={16} className={doc.source === "agent" ? "text-[#00A86B]" : "text-[#002FA7]"} />
                <span className="flex-1 truncate">{doc.name} <span className="text-xs text-[#888]">· {doc.source === "agent" ? "your deliverable" : "client upload"}</span></span>
                <button data-testid={`gtask-cdoc-dl-${i}-${di}`} onClick={() => downloadDoc(doc.id, doc.name)} className="text-[#002FA7] hover:bg-[#002FA7]/10 p-1 rounded-sm"><DownloadSimple size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`rounded-sm p-3 mb-3 flex items-center justify-between gap-3 flex-wrap ${done ? "bg-[#00A86B]/10 border border-[#00A86B]/30" : "bg-black/[0.02]"}`}>
        <p className="text-sm">{done ? <span className="text-[#00786f] font-medium flex items-center gap-1.5"><CheckCircle size={16} weight="fill" /> Job complete — submit the final document.</span> : "Submit the final deliverable when this job is done."}</p>
        <label data-testid={`gtask-deliver-${i}`} className="bg-[#00A86B] hover:brightness-95 text-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2 cursor-pointer">
          <Paperclip size={15} /> Submit final document<input type="file" hidden onChange={(e) => e.target.files[0] && deliver(e.target.files[0])} />
        </label>
      </div>

      <div className="grid md:grid-cols-4 gap-3 items-end border-t border-black/10 pt-3">
        <div><label className="label block mb-1">Progress %</label>
          <input data-testid={`gtask-progress-${i}`} type="number" min="0" max="100" value={d.progress} onChange={(e) => setD({ ...d, progress: e.target.value })} className={inputCls} /></div>
        <div><label className="label block mb-1">Status</label>
          <select data-testid={`gtask-status-${i}`} value={d.status} onChange={(e) => setD({ ...d, status: e.target.value })} className={inputCls}>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select></div>
        <div><label className="label block mb-1">Remarks</label>
          <input data-testid={`gtask-remarks-${i}`} value={d.remarks} onChange={(e) => setD({ ...d, remarks: e.target.value })} className={inputCls} /></div>
        <button data-testid={`gtask-save-${i}`} onClick={save} className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm flex items-center justify-center gap-2"><FloppyDisk size={16} /> Save</button>
      </div>
    </div>
  );
}

function NewTask({ sel, onCreated }) {
  const [nt, setNt] = useState({ service: "", category: "GST", due: "" });
  const create = async (e) => {
    e.preventDefault();
    if (!nt.service.trim()) { toast.error("Enter a task name"); return; }
    await api.post("/projects", { ...nt, client_email: sel });
    toast.success("Task created"); setNt({ service: "", category: nt.category, due: "" }); onCreated(nt.category);
  };
  return (
    <form onSubmit={create} className="card p-5 mb-6 grid sm:grid-cols-4 gap-3 items-end fade-up" data-testid="new-task-form">
      <div><label className="label block mb-1">Service</label>
        <select data-testid="nt-category" value={nt.category} onChange={(e) => setNt({ ...nt, category: e.target.value })} className={inputCls}>
          {[...SERVICE_LIST, "Other"].map((c) => <option key={c}>{c}</option>)}
        </select></div>
      <div><label className="label block mb-1">Task</label>
        <input data-testid="nt-service" value={nt.service} onChange={(e) => setNt({ ...nt, service: e.target.value })} placeholder="e.g. GSTR-3B July" className={inputCls} /></div>
      <div><label className="label block mb-1">Due date</label>
        <input data-testid="nt-due" type="date" value={nt.due} onChange={(e) => setNt({ ...nt, due: e.target.value })} className={inputCls} /></div>
      <button data-testid="nt-create" className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm">Create Task</button>
    </form>
  );
}

export default function AgentWorkspace() {
  const { sel, selClient } = useAgentClient();
  const [tasks, setTasks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [svc, setSvc] = useState(null);
  const [showNt, setShowNt] = useState(false);

  const load = () => api.get("/agent/tasks").then((r) => setTasks(r.data));
  const loadDocs = () => sel && api.get(`/agent/documents?client_email=${encodeURIComponent(sel)}`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { loadDocs(); setSvc(null); }, [sel]);

  const clientTasks = useMemo(() => tasks.filter((t) => t.client_email === sel), [tasks, sel]);
  const services = useMemo(() => [...new Set([...(selClient?.services || []), ...clientTasks.map((t) => t.category || "Other")])], [selClient, clientTasks]);
  const active = svc && services.includes(svc) ? svc : services[0];
  const items = clientTasks.filter((t) => (t.category || "Other") === active);
  const svcDocs = docs.filter((d) => (d.service || d.folder) === active);
  const meta = metaFor(active);

  return (
    <div>
      <PageHead eyebrow={selClient ? `Deliver Client Work · ${selClient.company}` : "Deliver Client Work"} title="Project Tracker">
        {selClient && <button data-testid="new-task-btn" onClick={() => setShowNt(!showNt)} className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2"><Plus size={16} /> New Task</button>}
      </PageHead>
      <NeedClient>
        {showNt && <NewTask sel={sel} onCreated={(c) => { setShowNt(false); setSvc(c); load(); }} />}
        {services.length === 0 ? <p className="card p-8 text-sm text-[#888] text-center">No services or jobs for this client yet. Create a task to start.</p> : (
          <>
            <p className="label mb-3">Services — select one to open its work</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="agent-service-buttons">
              {services.map((s) => {
                const m = metaFor(s);
                const on = s === active;
                const n = clientTasks.filter((t) => (t.category || "Other") === s).length;
                return (
                  <button key={s} data-testid={`asvc-btn-${s.toLowerCase()}`} onClick={() => setSvc(s)}
                    className={`text-left p-4 rounded-sm border-2 transition-[transform,background-color] hover:-translate-y-0.5 ${on ? "text-white" : "bg-white hover:bg-black/[0.02]"}`}
                    style={{ borderColor: m.color, background: on ? m.color : undefined }}>
                    <m.Icon size={24} weight="fill" style={{ color: on ? "#fff" : m.color }} />
                    <p className="font-head font-bold mt-2">{s}</p>
                    <p className={`text-xs ${on ? "text-white/80" : "text-[#888]"}`}>{n} job(s)</p>
                  </button>
                );
              })}
            </div>
            <div className="card p-5 mb-4 flex items-center gap-3" style={{ borderLeft: `4px solid ${meta.color}` }} data-testid="agent-service-panel">
              <meta.Icon size={26} weight="fill" style={{ color: meta.color }} />
              <div><p className="font-head font-black text-xl">{active}</p><p className="text-xs text-[#888]">{items.length} job(s) · {svcDocs.length} record(s)</p></div>
            </div>
            <div className="space-y-4">
              {items.map((t, i) => (
                <JobCard key={t.id} t={t} i={i} jobDocs={svcDocs} reload={load} refreshDocs={loadDocs}
                  onSaved={(data) => setTasks((p) => p.map((x) => (x.id === data.id ? data : x)))} />
              ))}
              {items.length === 0 && <p className="card p-6 text-sm text-[#888]">No jobs under {active} yet.</p>}
            </div>
          </>
        )}
      </NeedClient>
    </div>
  );
}
